// src/components/payment/PurchaseComponent.js

import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { apiFetch } from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { BASE_URL } from '../../utils/config'; // Using your established BASE_URL
import { CartContext } from '../../context/CartContext';
import { getSessionId } from '../topbar/SessionTracker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import './PurchaseComponent.css';

/**
 * A reusable component to handle the purchase of a product.
 * @param {object} props
 * @param {string} props.productId - The ID of the product to purchase (e.g., "report").
 * @param {string} [props.propertyId] - The ID of the property this purchase relates to.
 * @param {string} [props.inquiryId] - The ID of the inquiry this purchase relates to. // NEW: Added to docstring
 * @param {string} props.buyNowText - The text for the "Buy Now" button.
 * @param {function} props.onPurchaseSuccess - A callback function to run after a successful purchase.
 * @param {boolean} [props.isDisabled] - Whether the purchase button should be disabled.
 * @param {function} [props.onAddToCartSuccess] - Optional callback after adding to cart.
 */
const PurchaseComponent = ({ productId, propertyId = null, inquiryId = null, buyNowText = 'Buy Now', onPurchaseSuccess, isDisabled = false, onAddToCartSuccess }) => {
  const [isBuyNowLoading, setIsBuyNowLoading] = useState(false);
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [itemInCart, setItemInCart] = useState(false);
  const { user, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { cart, updateCartState } = useContext(CartContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();

  const isLoading = isBuyNowLoading || isAddToCartLoading;

  // Check if the item is in the cart when the component mounts or the cart changes.
  useEffect(() => {
    if (productId === 'report' && propertyId) {
      const isInCart = cart.items.some((item) => item.productType === 'report' && item.propertyId === String(propertyId));
      setItemInCart(isInCart);
    }
    // We could add checks for other product types here if needed.
  }, [cart, productId, propertyId]);

  const handleBuyNow = async (forceRepurchase = false) => {
    setIsBuyNowLoading(true);
    try {
      const url = `${BASE_URL}/api/payments/purchase`;
      const payload = { productId, propertyId, inquiryId, forceRepurchase, sessionId: getSessionId() };
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      };

      const response = await apiFetch(url, options, user.token);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `An error occurred: ${response.statusText}`);
      }

      // Handle confirmation flow
      if (result.status === 'confirmation_needed') {
        addNotification(result.message, 'warning', 0, true, [
          { text: t('purchaseComponent.cancel'), onClick: () => {} },
          { text: t('purchaseComponent.yesRepurchase'), onClick: () => handleBuyNow(true), className: 'primary' },
        ]);
        setIsBuyNowLoading(false); // Stop loading while waiting for user confirmation
        return;
      }

      // Handle success flow
      if (result.success) {
        // The backend now sends a user object with updated points.
        if (result.user) {
          updateUser({
            creditPoints: result.user.creditPoints,
            pointsOnHold: result.user.pointsOnHold,
          });
        }
        if (result.paymentNeeded) {
          //          addNotification('Payment required. Please select a payment method.', 'info');
          navigate(`/${lang}/select-payment?orderId=${result.orderId}`);
        } else {
          addNotification(t('purchaseComponent.purchaseSuccess'), 'success', 3000);
          setTimeout(() => {
            navigate(`/${lang}/my-orders`);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      addNotification(t('purchaseComponent.purchaseFailed', { message: error.message }), 'error');
      setIsBuyNowLoading(false);
    }
    // Note: We don't set loading to false on success because we either navigate away
    // or the parent component's state change will unmount this one.
  };

  const handleAddToCart = async (forceRepurchase = false) => {
    // Log when the function is called and with what arguments.
    console.log('[PurchaseComponent] handleAddToCart triggered.', { productId, propertyId, inquiryId, forceRepurchase });
    setIsAddToCartLoading(true);
    try {
      const payload = { productId, propertyId, inquiryId, forceRepurchase };
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, // This header is important for the backend to parse the body correctly.
        body: JSON.stringify(payload),
      };
      const response = await apiFetch(`${BASE_URL}/api/cart/items`, options, user.token);
      const data = await response.json();

      if (!response.ok) {
        // Handle specific errors like 'already in cart'
        addNotification(data.message || t('purchaseComponent.addToCart.failed'), 'error');
        return;
      }

      // Handle the new confirmation flow
      if (data.status === 'confirmation_needed') {
        addNotification(data.message, 'warning', 0, true, [
          { text: t('purchaseComponent.cancel'), onClick: () => {} },
          { text: t('purchaseComponent.yesRepurchase'), onClick: () => handleAddToCart(true), className: 'primary' },
        ]);
      } else {
        // Standard success: item was added to cart
        updateCartState(data);
        const itemName = data.items[data.items.length - 1]?.name || t('purchaseComponent.addToCart.itemFallback');
        addNotification(t('purchaseComponent.addToCart.success', { itemName }), 'success');
        setItemInCart(true);
        // We no longer call onAddToCartSuccess here; it's now used for the "Continue Shopping" button.
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      addNotification(t('purchaseComponent.addToCart.networkError'), 'error');
    } finally {
      setIsAddToCartLoading(false);
    }
  };

  return (
    <div className="purchase-container">
      {!itemInCart ? (
        <>
          <button className="purchase-button add-to-cart-btn" onClick={() => handleAddToCart()} disabled={isLoading || isDisabled} title={t('purchaseComponent.addToCart.title')}>
            {isAddToCartLoading ? t('purchaseComponent.adding') : t('purchaseComponent.addToCart.button')}
          </button>
          <button className="purchase-button buy-now-btn" onClick={() => handleBuyNow(false)} disabled={isLoading || isDisabled}>
            {isBuyNowLoading ? t('purchaseComponent.processing') : buyNowText}
          </button>
        </>
      ) : (
        <>
          <button className="purchase-button continue-shopping-btn" onClick={onAddToCartSuccess} disabled={isLoading}>
            {t('purchaseComponent.continueShopping')}
          </button>
          <button className="purchase-button go-to-cart-btn" onClick={() => navigate(`/${lang}/cart`)} disabled={isLoading} title={t('purchaseComponent.goToCart.title')}>
            <FontAwesomeIcon icon={faShoppingCart} /> {t('purchaseComponent.goToCart.button')}
          </button>
        </>
      )}
    </div>
  );
};

export default PurchaseComponent;
